import React, { useEffect, useMemo, useState } from "react";
import { matchesDateRange } from "../../../OperationsJournal/mock/operationsJournalMock";
import PeriodFilter from "../../../OperationsJournal/components/PeriodFilter/PeriodFilter";
import JournalPagination from "../../../OperationsJournal/components/JournalPagination/JournalPagination";
import { Checkbox, SortIcon } from "../../../OperationsJournal/components/tableParts";
import SelectionBar, { SelectionStat } from "../SelectionBar/SelectionBar";
import AllocationInfo from "../AllocationInfo/AllocationInfo";
import {
  ALLOCATION_ROLES,
  BUILDING_TYPE_OPTIONS,
  CUSTOMER_OPTIONS,
  DEPARTMENT_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  RECIPIENT_PROJECTS,
} from "../../mock/recipientProjectsMock";
import "./RecipientsStep.css";

const PAGE_SIZE = 10;

const EMPTY_FILTERS = {
  search: "",
  department: "",
  projectType: "",
  buildingType: "",
  customer: "",
  role: "",
  startFrom: null,
  startTo: null,
  endFrom: null,
  endTo: null,
};

const SELECT_FILTERS = [
  { key: "department", placeholder: "Отделение", options: DEPARTMENT_OPTIONS },
  { key: "projectType", placeholder: "Тип проекта", options: PROJECT_TYPE_OPTIONS },
  { key: "buildingType", placeholder: "Тип здания", options: BUILDING_TYPE_OPTIONS },
  { key: "customer", placeholder: "Заказчик", options: CUSTOMER_OPTIONS },
];

const ROLE_OPTIONS = Object.entries(ALLOCATION_ROLES).map(([value, role]) => ({
  value,
  label: role.label,
}));

const isSelectable = (project) => ALLOCATION_ROLES[project.role].selectable;

const matchesFilters = (project, filters) => {
  const query = filters.search.trim().toLowerCase();
  if (
    query &&
    !project.code.toLowerCase().includes(query) &&
    !project.name.toLowerCase().includes(query)
  ) {
    return false;
  }
  if (SELECT_FILTERS.some(({ key }) => filters[key] && project[key] !== filters[key])) {
    return false;
  }
  if (filters.role && project.role !== filters.role) return false;
  if (!matchesDateRange(project.startDate, filters.startFrom, filters.startTo)) return false;
  return matchesDateRange(project.endDate, filters.endFrom, filters.endTo);
};

const SearchIcon = () => (
  <svg
    className="oj-search__icon"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8" r="5.5" stroke="#9AA4B2" strokeWidth="1.4" />
    <path d="M12.5 12.5L15.5 15.5" stroke="#9AA4B2" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

/**
 * Шаг 2: выбор проектов-получателей.
 * selected — Set id выбранных проектов (хранится в мастере, чтобы не терять при переходах).
 */
function RecipientsStep({
  operationsCount,
  operationsTotalLabel,
  selected,
  onSelectedChange,
  onBack,
  onContinue,
}) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);

  const updateFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const filteredProjects = useMemo(
    () => RECIPIENT_PROJECTS.filter((project) => matchesFilters(project, filters)),
    [filters]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const hasActiveFilters = Object.keys(EMPTY_FILTERS).some(
    (key) => filters[key] !== EMPTY_FILTERS[key]
  );

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const rows = filteredProjects.slice(pageStart, pageStart + PAGE_SIZE);

  const selectableRows = rows.filter(isSelectable);
  const pageSelectedCount = selectableRows.filter((project) => selected.has(project.id)).length;
  const isPageChecked =
    selectableRows.length > 0 && pageSelectedCount === selectableRows.length;
  const selectableInFilter = filteredProjects.filter(isSelectable);
  const notSelectedInFilter = selectableInFilter.filter(
    (project) => !selected.has(project.id)
  );

  const setProjectsChecked = (projects, checked) => {
    const next = new Set(selected);
    projects.filter(isSelectable).forEach((project) => {
      if (checked) next.add(project.id);
      else next.delete(project.id);
    });
    onSelectedChange(next);
  };

  const toggleProject = (project) => {
    if (!isSelectable(project)) return;
    setProjectsChecked([project], !selected.has(project.id));
  };

  return (
    <div className="al-recipients">
      <h3 className="al-section-title">Выберите проекты-получатели</h3>

      <AllocationInfo totalLabel={operationsTotalLabel} operationsCount={operationsCount} />

      <div className="oj-filters">
        <div className="oj-filters__search-row">
          <div className="oj-search">
            <SearchIcon />
            <input
              type="text"
              className="oj-search__input"
              placeholder="Поиск по коду или наименованию проекта..."
              value={filters.search}
              onChange={(event) => updateFilters({ search: event.target.value })}
            />
          </div>
          <button
            type="button"
            className={`oj-reset-link${hasActiveFilters ? " is-active" : ""}`}
            onClick={() => setFilters(EMPTY_FILTERS)}
            disabled={!hasActiveFilters}
          >
            Сбросить Все
          </button>
        </div>

        <div className="oj-filters__row al-recipients__filters">
          {SELECT_FILTERS.map(({ key, placeholder, options }) => (
            <select
              key={key}
              className="oj-select"
              value={filters[key]}
              onChange={(event) => updateFilters({ [key]: event.target.value })}
            >
              <option value="">{placeholder}</option>
              {options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ))}

          <PeriodFilter
            placeholder="Дата начала"
            dateFrom={filters.startFrom}
            dateTo={filters.startTo}
            onChange={({ dateFrom, dateTo }) =>
              updateFilters({ startFrom: dateFrom, startTo: dateTo })
            }
          />
          <PeriodFilter
            placeholder="Дата окончания"
            dateFrom={filters.endFrom}
            dateTo={filters.endTo}
            onChange={({ dateFrom, dateTo }) =>
              updateFilters({ endFrom: dateFrom, endTo: dateTo })
            }
          />

          <select
            className="oj-select"
            value={filters.role}
            onChange={(event) => updateFilters({ role: event.target.value })}
          >
            <option value="">Роль в аллокации</option>
            {ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="oj-table-card">
        <div className="oj-table-scroll">
          <table className="oj-table al-recipients__table">
            <thead>
              <tr>
                <th className="oj-cell--check">
                  <Checkbox
                    checked={isPageChecked}
                    indeterminate={pageSelectedCount > 0 && !isPageChecked}
                    disabled={selectableRows.length === 0}
                    label="Выбрать все проекты на странице"
                    onChange={() => setProjectsChecked(selectableRows, !isPageChecked)}
                  />
                </th>
                <th>
                  Код проекта <SortIcon />
                </th>
                <th>Наименование</th>
                <th>Отделение</th>
                <th>Тип проекта</th>
                <th>Тип здания</th>
                <th>Заказчик</th>
                <th>
                  Дата начала <SortIcon />
                </th>
                <th>
                  Дата окончания <SortIcon />
                </th>
                <th>Роль в аллокации</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((project) => {
                const selectable = isSelectable(project);
                const isChecked = selected.has(project.id);
                return (
                  <tr
                    key={project.id}
                    className={[
                      "oj-row",
                      isChecked && "is-checked",
                      !selectable && "is-disabled",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    title={selectable ? undefined : "Проект исключён из распределения"}
                    onClick={() => toggleProject(project)}
                  >
                    <td className="oj-cell--check">
                      <Checkbox
                        checked={isChecked}
                        disabled={!selectable}
                        label={`Выбрать проект ${project.code}`}
                        onChange={() => toggleProject(project)}
                      />
                    </td>
                    <td className="al-recipients__code">{project.code}</td>
                    <td className="oj-cell--wrap al-recipients__name">{project.name}</td>
                    <td>{project.department}</td>
                    <td>{project.projectType}</td>
                    <td className="oj-cell--wrap">{project.buildingType}</td>
                    <td className="oj-cell--wrap">{project.customer}</td>
                    <td>{project.startDate}</td>
                    <td>{project.endDate}</td>
                    <td>
                      <span className={`al-role al-role--${project.role}`}>
                        {ALLOCATION_ROLES[project.role].label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <div className="oj-empty-state">По выбранным фильтрам проектов не найдено</div>
        )}

        <JournalPagination
          page={currentPage}
          totalPages={totalPages}
          shown={rows.length}
          total={filteredProjects.length}
          onChange={setCurrentPage}
        />
      </div>

      <SelectionBar
        onBack={onBack}
        continueDisabled={selected.size === 0}
        onContinue={onContinue}
      >
        <SelectionStat label="Выбрано проектов" value={selected.size} />
        {notSelectedInFilter.length > 0 && (
          <button
            type="button"
            className="al-link"
            onClick={() => setProjectsChecked(notSelectedInFilter, true)}
          >
            Выбрать все по фильтру ({selectableInFilter.length})
          </button>
        )}
        {selected.size > 0 && (
          <button
            type="button"
            className="al-link al-link--muted"
            onClick={() => onSelectedChange(new Set())}
          >
            Снять выбор
          </button>
        )}
      </SelectionBar>
    </div>
  );
}

export default RecipientsStep;
